import { findStrategy, hashInvestment } from "../../component/Methods";

export function getPositionStableRowId(position) {
  const cust = position.custodian ?? "no-custodian";
  const sym = position.tickerSymbol ?? "";
  const strat = position.strategy ?? "no-strategy";
  return `${position.fund}_${cust}_${sym}_${strat}`;
}

const emptyDisplay = "-";

function searchValue(value) {
  if (value == null || value === "") return emptyDisplay;
  return String(value).toLowerCase().trim();
}

function digitsOnly(value) {
  return String(value ?? "").replace(/[^\d.]/g, "");
}

function moneySearchValues(value) {
  if (value == null || value === "") return [emptyDisplay];
  const amount = Number(value);
  if (!Number.isFinite(amount)) return [emptyDisplay];
  const fixed = amount.toFixed(2);
  return [`$${fixed}`, fixed, String(amount)];
}

function sharesSearchValue(shares) {
  if (shares == null || shares === "") return emptyDisplay;
  const n = Number(shares);
  if (!Number.isFinite(n) || n === 0) return emptyDisplay;
  return n < 0
    ? `(${Math.abs(n).toLocaleString()})`
    : n.toLocaleString();
}

function positionMatchesSearch(item, searchLower) {
  const textFields = [
    searchValue(item.positionType),
    searchValue(item.tickerSymbol),
    searchValue(item.investmentType),
    searchValue(item.fundName),
    searchValue(item.strategyName),
    searchValue(item.custodianName),
    sharesSearchValue(item.shares).toLowerCase(),
    ...moneySearchValues(item.averageCostPerShare),
    ...moneySearchValues(item.price),
  ];

  if (textFields.some((field) => field.includes(searchLower))) return true;

  const queryDigits = digitsOnly(searchLower);
  if (queryDigits && /\d/.test(searchLower)) {
    const numericFields = [
      digitsOnly(item.shares),
      digitsOnly(Math.abs(Number(item.shares) || 0)),
      digitsOnly(item.averageCostPerShare),
      digitsOnly(item.price),
      digitsOnly(Number(item.averageCostPerShare).toFixed(2)),
      digitsOnly(Number(item.price).toFixed(2)),
      digitsOnly(sharesSearchValue(item.shares)),
    ];
    return numericFields.some((field) => field && field.includes(queryDigits));
  }

  return false;
}

export function sortPositionsList(positions, sortField, sortDirection) {
  return [...positions].sort((a, b) => {
    let aValue = a[sortField];
    let bValue = b[sortField];

    if (sortField === "tickerSymbol") {
      const aStartsWithNumber = /^\d/.test((aValue || "").toLowerCase());
      const bStartsWithNumber = /^\d/.test((bValue || "").toLowerCase());

      if (aStartsWithNumber && !bStartsWithNumber) return 1;
      if (!aStartsWithNumber && bStartsWithNumber) return -1;

      aValue = (aValue || "").toLowerCase();
      bValue = (bValue || "").toLowerCase();
    } else if (
      sortField === "shares" ||
      sortField === "averageCostPerShare" ||
      sortField === "price"
    ) {
      aValue = parseFloat(aValue) || 0;
      bValue = parseFloat(bValue) || 0;
    } else {
      aValue = (aValue || "").toLowerCase();
      bValue = (bValue || "").toLowerCase();
    }

    if (aValue < bValue) return sortDirection === "asc" ? -1 : 1;
    if (aValue > bValue) return sortDirection === "asc" ? 1 : -1;
    return 0;
  });
}

export function buildDisplayedPositions({
  originalPositions,
  selectedFund,
  selectedStrategy,
  search,
  sortField,
  sortDirection,
}) {
  let filtered = (originalPositions || []).map((position) => ({
    ...position,
    investmentType: hashInvestment(Number(position.investment)) || "-",
  }));

  if (selectedFund) {
    filtered = filtered.filter((item) => item.fund === selectedFund);
  }

  if (selectedStrategy) {
    filtered = filtered.filter((item) => item.strategy === selectedStrategy);
  }

  if (search) {
    const searchLower = search.trim().toLowerCase();
    if (searchLower) {
      filtered = filtered.filter((item) =>
        positionMatchesSearch(item, searchLower)
      );
    }
  }

  const sortedFiltered = sortPositionsList(filtered, sortField, sortDirection);

  return sortedFiltered.map((position) => ({
    ...position,
    id: getPositionStableRowId(position),
  }));
}

export function getStrategiesWithPositiveAum({
  accountingFunds,
  selectedFundId,
  strategies,
}) {
  if (!selectedFundId || !Array.isArray(accountingFunds) || accountingFunds.length === 0) {
    return [];
  }
  const accFund = accountingFunds.find((row) => row?.fund === selectedFundId);
  const aums = accFund?.strategyAUMs;
  if (!aums || typeof aums !== "object") return [];

  return Object.entries(aums)
    .filter(([, aum]) => Number(aum) > 0)
    .map(([strategyId]) => ({
      strategyId,
      name: findStrategy(strategyId, strategies) || strategyId,
    }))
    .sort((a, b) => (a.name || "").localeCompare(b.name || ""));
}

/** Fund ids that appear on at least one position row. */
export function getFundIdsWithPositions(positions) {
  const ids = new Set();
  (positions || []).forEach((p) => {
    if (p?.fund) ids.add(p.fund);
  });
  return ids;
}

export function filterFundsWithPositions(funds, positions) {
  if (!Array.isArray(funds)) return [];
  const withPos = getFundIdsWithPositions(positions);
  return funds.filter((f) => f?.fundId && withPos.has(f.fundId));
}

/**
 * Strategies with accounting AUM &gt; 0 for the fund, intersected with strategies
 * that actually have position rows for that fund.
 */
export function getStrategiesWithPositiveAumAndPositions({
  accountingFunds,
  selectedFundId,
  strategies,
  positions,
}) {
  const positive = getStrategiesWithPositiveAum({
    accountingFunds,
    selectedFundId,
    strategies,
  });
  if (!selectedFundId || !Array.isArray(positions)) {
    return positive;
  }

  const strategyIdsWithPositions = new Set(
    positions
      .filter((p) => p?.fund === selectedFundId && p?.strategy)
      .map((p) => p.strategy)
  );
  return positive.filter((s) => strategyIdsWithPositions.has(s.strategyId));
}

/** WebSocket action for fund+strategy close (preview uses submit: false). */
export const CLOSE_STRATEGY_ACTION = "close_strategy";
export function buildCloseStrategyWebSocketPayload(fundId, strategyId, submit = false) {
  return {
    action: CLOSE_STRATEGY_ACTION,
    data: {
      fundId,
      strategyId,
      submit,
    },
  };
}
